import { useQueryClient } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../lib/api";

type ConnectionState = "offline" | "connecting" | "online" | "error";

interface RealtimeValue {
  state: ConnectionState;
  socket: Socket | null;
}

const RealtimeContext = createContext<RealtimeValue>({ state: "offline", socket: null });

function socketOrigin(): string {
  if (/^https?:\/\//u.test(api.baseUrl)) return new URL(api.baseUrl).origin;
  return window.location.origin;
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [state, setState] = useState<ConnectionState>("offline");

  useEffect(() => {
    if (auth.state.kind !== "authenticated" || auth.state.viewer.status !== "ACTIVE") {
      setState("offline");
      setSocket(null);
      return;
    }
    setState("connecting");
    const connection = io(socketOrigin(), {
      path: (import.meta.env.VITE_SOCKET_PATH as string | undefined) ?? "/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
      autoConnect: true,
    });
    setSocket(connection);

    const refreshAll = () => {
      void queryClient.invalidateQueries();
    };
    connection.on("connect", () => setState("online"));
    connection.on("disconnect", () => setState("offline"));
    connection.on("connect_error", () => setState("error"));
    connection.on("notification", () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    });
    connection.on("notification:new", () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    });
    connection.on("wallet.update", () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["account"] });
    });
    connection.on("game.state", refreshAll);
    connection.on("game.finished", refreshAll);
    connection.on("table:state", refreshAll);
    connection.on("chat:message", refreshAll);
    connection.on(
      "vote.updated",
      () => void queryClient.invalidateQueries({ queryKey: ["votes"] }),
    );

    return () => {
      connection.removeAllListeners();
      connection.close();
      setSocket(null);
      setState("offline");
    };
  }, [auth.state, queryClient]);

  const value = useMemo(() => ({ state, socket }), [state, socket]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeValue {
  return useContext(RealtimeContext);
}
